/* eslint-disable no-shadow */
import {
  Box,
  Checkbox,

  Grid,
  MenuItem,
  Typography,
  useTheme,
} from '@mui/material';
import { blueGrey } from '@mui/material/colors';
import { useState, useEffect } from 'react';
import {
  Controller, useFormContext,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { selectMemberInformation } from '../../../../store/slices/onboardingSlice';
import { useAppSelector } from '../../../../store/hooks';
import { ValidFieldPattern } from '../../../Onboarding/constants';
import {
  EditOption, ErrorAlert, IFormInputs, StyledSelect, StyledTextField,
} from '../../../../common/StyledFormInputs';
import { Body8VariantTypography } from '../../../../common/TypographyVariants';
import { CompanyBusinessType } from '../../../../common/enums';
import { getFullAddressUS, isValidWebsite, isValidPhoneNumber } from '../../../../common/utils/formUtils';
import { AddressAutoCompleteInput } from '../../../../common/AddressAutoCompleteInput';
import { TestIds } from '../../../../assets/testIds';

interface BusinessInformationFormControlProps {
  hasEmptyFields?: boolean;
  isHdSupplyForm?: boolean;
  isProfileForm?: boolean;
}

export function BusinessInformationFormControl(
  {
    isHdSupplyForm = true, hasEmptyFields = false,
    isProfileForm = false,
  }: BusinessInformationFormControlProps,
) {
  const theme = useTheme();
  const { t } = useTranslation();
  const memberInfo = useAppSelector(selectMemberInformation);

  const [editBusinessAddress, setEditBusinessAddress] = useState(hasEmptyFields);
  const [isBillingAndBusinessAddressSame, setIsBillingAndBusinessAddressSame] = useState(true);
  const {
    control, register, formState, clearErrors, trigger, getFieldState,
  } = useFormContext<IFormInputs>();
  const { errors, submitCount } = formState;
  const addressState = getFieldState('businessInfo.autoAddress', formState);
  const addressFromDatabase = submitCount > 0 || addressState.isTouched || addressState.isDirty;

  // Trigger validation on component mount
  // Wrong values could be created through Retool or Joist
  useEffect(() => {
    if (editBusinessAddress) {
      trigger(['businessInfo.website', 'businessInfo.name', 'businessInfo.phone', 'businessInfo.email', 'businessInfo.autoAddress']);
    }
  }, [editBusinessAddress, trigger]);

  const handleOnChange = () => {
    clearErrors('businessInfo.businessType');
  };

  return (
    <>
      {!isProfileForm &&
        <Grid container spacing={2} mt={theme.spacing(1)}>
          <Grid item xs={8} sm={10} md={10} lg={10}>
            <Typography variant="h2" gutterBottom>
              {t('businessInformationForm.heading')}
            </Typography>
          </Grid>
          {!editBusinessAddress &&
            <Grid item xs={4} sm={2} md={2} lg={2}>
              <EditOption
                updateEditState={(newState: boolean) => setEditBusinessAddress(newState)} />
            </Grid>
          }
        </Grid>
      }
      {editBusinessAddress ? <Box
        sx={{
          mt: theme.spacing(1),
          mb: theme.spacing(2),
        }}
      >
        <Box sx={{ mb: theme.spacing(1) }}>
          <Body8VariantTypography>
            {`${t('businessInformationForm.name', {
              businessNamePrefix: isHdSupplyForm ? 'Legal business' : 'Business',
            })}*` || ''}
          </Body8VariantTypography>
          <StyledTextField
            data-testid={TestIds.rewardsPage.signUpForm.businessNameTxt}
            placeholder='Name'
            {...register('businessInfo.name', { required: true })}
          />
          {(errors.businessInfo?.name) && <ErrorAlert />}
        </Box>
        <Box sx={{ mb: theme.spacing(1) }}>
          <Body8VariantTypography>{`${t('businessInformationForm.phone')}*` || ''}</Body8VariantTypography>
          <StyledTextField
            data-testid={TestIds.rewardsPage.signUpForm.companyPhoneTxt}
            placeholder='Phone'
            {...register('businessInfo.phone', {
              required: true,
              validate: (value) => isValidPhoneNumber(value) || `${t('error.invalidPhoneNumber')}`,
            })}
          />
          {(errors.businessInfo?.phone) &&
            <ErrorAlert errorMessage={errors.businessInfo?.phone?.message} />}
        </Box><Box sx={{ mb: theme.spacing(1) }}>
          <Body8VariantTypography>{`${t('businessInformationForm.email')}*` || ''}</Body8VariantTypography>
          <StyledTextField
            data-testid={TestIds.rewardsPage.signUpForm.companyEmailTxt}
            placeholder='Email'
            {...register('businessInfo.email', {
              required: true,
              pattern: {
                value: ValidFieldPattern.validEmailPattern,
                message: `${t('error.invalidEmail2')}`,
              },
            })}
          />
          {(errors.businessInfo?.email) && <ErrorAlert
            errorMessage={errors.businessInfo?.email?.message} />}
        </Box>
        {!isHdSupplyForm && <Box sx={{ mb: theme.spacing(1) }}>
          <Body8VariantTypography>{`${t('businessInformationForm.website')}` || ''}</Body8VariantTypography>
          <StyledTextField
            data-testid={TestIds.rewardsPage.signUpForm.websiteTxt}
            placeholder='Website'
            {...register('businessInfo.website', {
              required: false,
              validate: (value) => {
                if (!value) return true; // Empty is okay
                return isValidWebsite(value) ? true : t('error.invalidWebsiteUrl') as string;
              },
            })}
          />
          {(errors.businessInfo?.website) &&
            <ErrorAlert errorMessage={errors.businessInfo?.website?.message} />}
        </Box>
        }
        <Box sx={{ mb: theme.spacing(1) }}>
          <Body8VariantTypography>{`${t('businessInformationForm.address.heading')}*` || ''}</Body8VariantTypography>
          <AddressAutoCompleteInput
            formFieldName={'businessInfo.autoAddress'}
            // clearErrors={clearErrors}
            // control={control}
            placeholderText={'Business address'}
          />
          {errors.businessInfo?.autoAddress && (
            <ErrorAlert
              errorMessage={
                (addressFromDatabase) ?
                  `${t('error.addressField.required')}` :
                  `${t('error.addressField.confirm')}`
              }
            />
          )}
        </Box>
      </Box> : <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          mb: theme.spacing(2),
          mt: theme.spacing(1),
        }}
      >
        <Typography variant='body8' lineHeight={2}>
          <span style={{ fontSize: theme.spacing(1.75), fontWeight: 700 }}>
            {`${t('businessInformationForm.name', {
              businessNamePrefix: isHdSupplyForm ? 'Legal business' : 'Business',
            })}*` || ''}: </span>
          {memberInfo.companyName}
        </Typography>
        <Typography variant='body8' lineHeight={2}>
          <span style={{ fontSize: theme.spacing(1.75), fontWeight: 700 }}>
            {t('businessInformationForm.phone')}: </span>
          {memberInfo.companyPhoneNumber}
        </Typography>
        <Typography variant='body8' lineHeight={2}>
          <span style={{ fontSize: theme.spacing(1.75), fontWeight: 700 }}>
            {t('businessInformationForm.email')}: </span>
          {memberInfo.companyEmail}
        </Typography>
        {!isHdSupplyForm &&
          <Typography variant='body8' lineHeight={2}>
            <span style={{ fontSize: theme.spacing(1.75), fontWeight: 700 }}>
              {t('businessInformationForm.website')}: </span>
            {memberInfo.companyWebsiteUrl}
          </Typography>
        }
        <Typography variant='body8' lineHeight={2}>
          <span style={{ fontSize: theme.spacing(1.75), fontWeight: 700 }}>
            {t('businessInformationForm.address.heading')}: </span>
          {memberInfo.companyFullAddress || getFullAddressUS(
            memberInfo?.companyAddressStreet,
            memberInfo.companyAddressCity,
            memberInfo.companyAddressState,
            memberInfo.companyAddressZipCode,
          )}
        </Typography>
      </Box>}
      {isHdSupplyForm && <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Box>
          <Controller
            name="businessInfo.sameBusinessAndBillingAddress"
            control={control}
            render={({ field: props }) => (
              <Checkbox
                icon={<Box sx={{
                  border: `2px solid ${blueGrey[200]}`,
                  width: theme.spacing(2.25),
                  height: theme.spacing(2.25),
                }}></Box>}
                sx={{
                  '&.MuiCheckbox-root': {
                    paddingLeft: theme.spacing(0),
                    width: theme.spacing(4.125),
                    height: theme.spacing(4.125),
                    '&.Mui-checked': {
                      color: theme.palette.brand.primary,
                    },
                  },
                }}
                {...props}
                onChange={
                  (e) => {
                    props.onChange(e);
                    setIsBillingAndBusinessAddressSame(e.target.checked);
                  }
                }
                checked={isBillingAndBusinessAddressSame}
              />
            )}
          />
        </Box>
        <Body8VariantTypography>
          {t('businessInformationForm.billingAddress.sameAsBusinessAddress') || ''}
        </Body8VariantTypography>
      </Box>}
      {isHdSupplyForm && !isBillingAndBusinessAddressSame &&
        <Box sx={{ mb: theme.spacing(2) }}>
          <Body8VariantTypography>{`${t('businessInformationForm.billingAddress.heading')}*` || ''}</Body8VariantTypography>
          <AddressAutoCompleteInput
            formFieldName={'businessInfo.billingAutoAddress'}
            // clearErrors={clearErrors}
            // control={control}
            placeholderText={'Billing address'}
          />
          {errors.businessInfo?.autoAddress && (
            <ErrorAlert
              errorMessage={
                (addressFromDatabase) ?
                  `${t('error.addressField.required')}` :
                  `${t('error.addressField.confirm')}`
              }
            />
          )}
        </Box>
      }
      {isHdSupplyForm && <Box
        sx={{
          mt: theme.spacing(1),
        }}
      >
        <Typography variant='body8'
          sx={{
            marginBottom: theme.spacing(6),
          }}
        >{t('businessInformationForm.type')}*</Typography>
        <StyledSelect
          displayEmpty
          {...register('businessInfo.businessType', { required: true })}
          onChange={(e) => { handleOnChange(); }}
          defaultValue=''
        >
          <MenuItem disabled value=''>
            Select business type
          </MenuItem>
          {Object.values(CompanyBusinessType).map((option) => (
            <MenuItem
              key={option}
              value={option}
            >
              {option}
            </MenuItem>
          ))}
        </StyledSelect>
        {(errors.businessInfo?.businessType) && <ErrorAlert />}
      </Box>}
    </>
  );
}
